import { ToolOutlined } from '@ant-design/icons';
import {
  Card,
  Col,
  ConfigProvider,
  Layout,
  Row,
  theme,
  Typography,
} from 'antd';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import backgroundImage from '../../Assets/Images/Babes.png';
import logo from '../../Assets/Images/Logo_Math_ComputerScience.png';
import { useTheme } from '../../context/ThemeContext';
import { AppFooter } from '../AppFooter';
import { MaintenanceHeader } from './MaintenanceHeader';

const { Content } = Layout;

// The same page as the login page: the photo of the university, the header, the glass card and the footer.
const MaintenanceContent = (): ReactElement => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  return (
    <Layout
      style={{
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <MaintenanceHeader />

      <Content
        style={{
          flex: 1,
          position: 'relative',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        <Row
          justify="center"
          align="middle"
          style={{ minHeight: '100%', position: 'relative', zIndex: 1 }}
        >
          <Col xs={22} sm={18} md={14} lg={10} xl={8}>
            <Card
              style={{
                textAlign: 'center',
                background: isDarkMode
                  ? 'rgba(0, 0, 0, 0.66)'
                  : 'rgba(30, 41, 59, 0.55)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                padding: '20px 10px',
              }}
            >
              <img
                src={logo}
                alt="FMI Logo"
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '16px',
                  filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.3))',
                }}
              />

              <Typography.Title
                level={3}
                style={{
                  color: '#ffffff',
                  margin: 0,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                <ToolOutlined style={{ marginRight: 10 }} />
                {t('maintenance.title')}
              </Typography.Title>

              <Typography.Paragraph
                style={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: '15px',
                  marginTop: '12px',
                  marginBottom: 0,
                }}
              >
                {t('maintenance.description')}
              </Typography.Paragraph>
            </Card>
          </Col>
        </Row>
      </Content>

      <AppFooter isDark={true} />
    </Layout>
  );
};

// Shown instead of the application while the database is worked on. It stands on its own: the application, with its
// sign-in and its data, is not loaded behind it. The gate opens the application by itself when the maintenance is over.
export const MaintenancePage = (): ReactElement => {
  const { isDarkMode } = useTheme();

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: isDarkMode ? '#758cd7' : '#214175',
          ...(isDarkMode ? { colorPrimaryActive: '#112cc5' } : {}),
        },
      }}
    >
      <MaintenanceContent />
    </ConfigProvider>
  );
};
